/**
 * Laguna Beach Run Club - Global JavaScript
 * Core functionality and utilities
 */

// Debounce utility
function debounce(fn, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

// Throttle utility
function throttle(fn, limit) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Serialize form data
function serialize(form) {
  const formData = new FormData(form);
  const data = {};
  for (const [key, value] of formData.entries()) {
    data[key] = value;
  }
  return data;
}

// Fetch wrapper with error handling
async function fetchJSON(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

// Format money
function formatMoney(cents, format = window.LBRC?.moneyFormat) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

// Trap focus within element
function trapFocus(element) {
  const focusableElements = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        e.preventDefault();
      }
    }
  }

  element.addEventListener('keydown', handleKeydown);
  firstFocusable?.focus();

  return () => element.removeEventListener('keydown', handleKeydown);
}

// Header scroll behavior
class StickyHeader {
  constructor(element) {
    this.header = element;
    this.lastScrollY = 0;
    this.ticking = false;

    this.init();
  }

  init() {
    window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
  }

  onScroll() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  update() {
    const scrollY = window.scrollY;

    // Add scrolled class after 50px
    if (scrollY > 50) {
      this.header.classList.add('is-scrolled');
    } else {
      this.header.classList.remove('is-scrolled');
    }

    this.lastScrollY = scrollY;
  }
}

// Mobile Menu
class MobileMenu {
  constructor() {
    this.menu = document.querySelector('[data-mobile-menu]');
    this.toggleBtn = document.querySelector('[data-menu-toggle]');
    this.closeBtn = document.querySelector('[data-menu-close]');
    this.overlay = document.querySelector('.overlay');
    this.accordions = this.menu?.querySelectorAll('[data-accordion-toggle]');

    this.init();
  }

  init() {
    this.toggleBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // Accordion toggles
    this.accordions?.forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        const content = e.currentTarget.nextElementSibling;
        content?.classList.toggle('is-open');
        e.currentTarget.setAttribute(
          'aria-expanded',
          content?.classList.contains('is-open') ? 'true' : 'false'
        );
      });
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menu?.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    this.menu?.classList.add('is-open');
    this.overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    this.menu?.setAttribute('aria-hidden', 'false');
    trapFocus(this.menu);
  }

  close() {
    this.menu?.classList.remove('is-open');
    this.overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
    this.menu?.setAttribute('aria-hidden', 'true');
    this.toggleBtn?.focus();
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize sticky header
  const header = document.querySelector('[data-header]');
  if (header) {
    new StickyHeader(header);
  }

  // Initialize mobile menu
  new MobileMenu();
});

// Export utilities
window.LBRC = window.LBRC || {};
Object.assign(window.LBRC, {
  debounce,
  throttle,
  serialize,
  fetchJSON,
  formatMoney,
  trapFocus,
});
